/**
 * Finance Workbench Selectors
 * ───────────────────────────
 * Pure, read-only derivations powering Steve's day-to-day surfaces:
 *   • /finance/approvals   — unified authorize-here queue
 *   • /finance/collections — customer-grouped AR workbench
 *   • /finance/bills       — AP exposure derived from confirmed POs
 *   • /finance/close       — month-end checklist with derived states
 *
 * No new write paths. Every number ties to a canonical source row.
 */
import type { Job, Invoice } from "@/data/types";
import type { PayRunRow } from "@/data/finance/payRun/usePayRunViews";
import type { PayRunRecord } from "@/data/finance/payRun/recordPayRun";
import type { FieldAddSubmission } from "@/data/finance/payRun/fieldAdds";
import type { HoldbackState } from "@/data/contractor/networkExecution/holdback/deriveState";
import type { DisputeState } from "@/data/contractor/networkExecution/dispute/dispute";
import type { PurchaseOrder } from "@/data/VendorPOContext";

const MS_PER_DAY = 86_400_000;
const daysSince = (iso: string) =>
  Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / MS_PER_DAY));

// ───────────────────────────── Approvals Inbox ─────────────────────────

export type ApprovalKind =
  | "pay-run-held"
  | "field-add"
  | "holdback-release"
  | "dispute-open";

export type ApprovalUrgency = "critical" | "warn" | "info";

export interface ApprovalItem {
  id: string;
  kind: ApprovalKind;
  urgency: ApprovalUrgency;
  amount: number;
  title: string;
  subtitle: string;
  ageDays: number;
  jobId?: string;
  href: string;
  cta: string;
}

const heldUrgency = (ageDays: number): ApprovalUrgency =>
  ageDays >= 7 ? "critical" : ageDays >= 3 ? "warn" : "info";

export function buildApprovalsInbox(args: {
  heldRows: PayRunRow[];
  pendingFieldAdds: FieldAddSubmission[];
  holdbackStates: HoldbackState[];
  disputeStates: DisputeState[];
  jobs: Job[];
}): ApprovalItem[] {
  const { heldRows, pendingFieldAdds, holdbackStates, disputeStates, jobs } = args;
  const jobById = new Map(jobs.map((j) => [j.id, j]));
  const items: ApprovalItem[] = [];

  for (const row of heldRows) {
    const amt = row.view.baseAmount + row.view.fieldAddPendingTotal;
    const ageDays = row.heldAge ? Math.round(row.heldAge.ageMs / MS_PER_DAY) : 0;
    const reasons = row.view.holdReasons;
    items.push({
      id: `held-${row.job.id}`,
      kind: "pay-run-held",
      urgency: heldUrgency(ageDays),
      amount: amt,
      title: `${row.job.customerName} · Pay run held`,
      subtitle: reasons.length > 0 ? `${reasons.length} hold reason${reasons.length === 1 ? "" : "s"} · ${row.job.id}` : `Held · ${row.job.id}`,
      ageDays,
      jobId: row.job.id,
      href: "/finance/pay-runs?pivot=held",
      cta: "Review hold",
    });
  }

  for (const fa of pendingFieldAdds) {
    const job = jobById.get(fa.jobId);
    const ageDays = fa.createdAt ? daysSince(fa.createdAt) : 0;
    items.push({
      id: `fieldadd-${fa.id}`,
      kind: "field-add",
      urgency: ageDays >= 5 ? "warn" : "info",
      amount: fa.amount,
      title: `${job?.customerName ?? fa.jobId} · Field-add request`,
      subtitle: `${fa.reason ?? "Submitted by field"} · ${fa.jobId}`,
      ageDays,
      jobId: fa.jobId,
      href: "/finance/pay-runs?pivot=approvals",
      cta: "Approve",
    });
  }

  for (const hb of holdbackStates) {
    if (hb.status !== "active") continue;
    if (hb.pausedByDisputeId) continue;
    const ageDays = daysSince(hb.lastEntryAt);
    if (ageDays < 30) continue; // only surface holdbacks aged 30+ days for release review
    const job = jobById.get(hb.jobId);
    items.push({
      id: `holdback-${hb.holdbackId}`,
      kind: "holdback-release",
      urgency: ageDays >= 60 ? "warn" : "info",
      amount: hb.netHeld,
      title: `${job?.customerName ?? hb.jobId} · Holdback eligible`,
      subtitle: `Aged ${ageDays}d · ${hb.jobId}`,
      ageDays,
      jobId: hb.jobId,
      href: "/finance/pay-runs?pivot=held",
      cta: "Review",
    });
  }

  for (const d of disputeStates) {
    if (d.status !== "open") continue;
    const ageDays = daysSince(d.openedAt);
    const job = jobById.get(d.jobId);
    items.push({
      id: `dispute-${d.disputeId}`,
      kind: "dispute-open",
      urgency: ageDays >= 7 ? "critical" : "warn",
      amount: 0,
      title: `${job?.customerName ?? d.jobId} · Open dispute`,
      subtitle: `Opened ${ageDays}d ago · ${d.jobId}`,
      ageDays,
      jobId: d.jobId,
      href: `/jobs/${d.jobId}`,
      cta: "Investigate",
    });
  }

  const urgencyRank: Record<ApprovalUrgency, number> = { critical: 0, warn: 1, info: 2 };
  return items.sort((a, b) => {
    const u = urgencyRank[a.urgency] - urgencyRank[b.urgency];
    if (u !== 0) return u;
    return b.amount - a.amount;
  });
}

// ───────────────────────────── Collections Workbench ───────────────────

export type CollectionsBucket = "current" | "1-30" | "31-60" | "61-90" | "90+";

export interface CustomerCollectionsRow {
  customerName: string;
  jobIds: string[];
  phone: string;
  email: string;
  branch: string;
  openInvoices: number;
  openBalance: number;
  oldestDueDays: number;
  oldestInvoice: { id: string; amount: number; dueDate: string | null; jobId: string } | null;
  bucket: CollectionsBucket;
  lastPaymentDate: string | null;
}

const bucketFor = (days: number): CollectionsBucket =>
  days <= 0 ? "current"
  : days <= 30 ? "1-30"
  : days <= 60 ? "31-60"
  : days <= 90 ? "61-90"
  : "90+";

export function buildCollectionsRows(jobs: Job[]): CustomerCollectionsRow[] {
  const byCustomer = new Map<string, CustomerCollectionsRow>();

  for (const job of jobs) {
    const invs = job.financials?.invoices ?? [];
    if (invs.length === 0) continue;
    const key = job.customerName || job.id;
    const existing = byCustomer.get(key) ?? {
      customerName: job.customerName || "Unknown",
      jobIds: [],
      phone: job.phone || "",
      email: job.email || "",
      branch: job.branch || "",
      openInvoices: 0,
      openBalance: 0,
      oldestDueDays: 0,
      oldestInvoice: null,
      bucket: "current" as CollectionsBucket,
      lastPaymentDate: null,
    };
    if (!existing.jobIds.includes(job.id)) existing.jobIds.push(job.id);
    existing.phone = existing.phone || job.phone || "";
    existing.email = existing.email || job.email || "";

    for (const inv of invs) {
      if (inv.paidAt) {
        if (!existing.lastPaymentDate || inv.paidAt > existing.lastPaymentDate) {
          existing.lastPaymentDate = inv.paidAt;
        }
      }
      if (inv.status === "paid") continue;
      if (!inv.issuedAt) continue;
      const dueTs = inv.dueDate
        ? new Date(inv.dueDate).getTime()
        : new Date(inv.issuedAt).getTime() + 30 * MS_PER_DAY;
      const ageDays = Math.max(0, Math.round((Date.now() - dueTs) / MS_PER_DAY));
      existing.openInvoices += 1;
      existing.openBalance += inv.amount;
      if (ageDays > existing.oldestDueDays || !existing.oldestInvoice) {
        existing.oldestDueDays = ageDays;
        existing.oldestInvoice = {
          id: inv.id ?? `${job.id}-inv`,
          amount: inv.amount,
          dueDate: inv.dueDate ?? null,
          jobId: job.id,
        };
      }
    }
    existing.bucket = bucketFor(existing.oldestDueDays);
    byCustomer.set(key, existing);
  }

  return Array.from(byCustomer.values())
    .filter((r) => r.openBalance > 0)
    .sort((a, b) => {
      // Worst-first: oldest due days, then balance.
      if (b.oldestDueDays !== a.oldestDueDays) return b.oldestDueDays - a.oldestDueDays;
      return b.openBalance - a.openBalance;
    });
}

// ───────────────────────────── Bills (AP from POs) ─────────────────────

export type BillStatus = "confirmed" | "delivered" | "overdue";

export interface BillRow {
  poId: string;
  poNumber: string;
  vendorName: string;
  jobId: string;
  jobLabel: string;
  amount: number;
  status: BillStatus;
  /** Proxy due date: deliveredAt + 30d || confirmedAt + 45d. */
  dueDate: string;
  dueInDays: number; // negative = overdue
  ageDays: number;
}

const PAYMENT_TERMS_DELIVERED = 30; // Net-30 from delivery
const PAYMENT_TERMS_CONFIRMED = 45; // not yet delivered → push out

export function buildBillsFromPOs(pos: PurchaseOrder[]): BillRow[] {
  const rows: BillRow[] = [];
  for (const po of pos) {
    if (po.status !== "Confirmed" && po.status !== "Delivered") continue;
    if (po.finance?.paymentState === "paid") continue;
    const isDelivered = po.status === "Delivered";
    const anchorIso = isDelivered ? po.deliveredAt : po.confirmedAt;
    if (!anchorIso) continue;
    const anchorTs = new Date(anchorIso).getTime();
    const termsDays = isDelivered ? PAYMENT_TERMS_DELIVERED : PAYMENT_TERMS_CONFIRMED;
    const dueTs = anchorTs + termsDays * MS_PER_DAY;
    const dueInDays = Math.round((dueTs - Date.now()) / MS_PER_DAY);
    const ageDays = Math.round((Date.now() - anchorTs) / MS_PER_DAY);
    const status: BillStatus =
      dueInDays < 0 ? "overdue"
      : isDelivered ? "delivered"
      : "confirmed";
    rows.push({
      poId: po.poId,
      poNumber: po.poNumber,
      vendorName: po.vendorName,
      jobId: po.jobId,
      jobLabel: po.jobLabel,
      amount: po.totalCost,
      status,
      dueDate: new Date(dueTs).toISOString(),
      dueInDays,
      ageDays,
    });
  }
  return rows.sort((a, b) => a.dueInDays - b.dueInDays);
}

export interface VendorBillsRollup {
  vendorName: string;
  openBills: number;
  totalDue: number;
  overdueCount: number;
  overdueAmount: number;
  nextDueInDays: number;
}

export function rollupBillsByVendor(bills: BillRow[]): VendorBillsRollup[] {
  const map = new Map<string, VendorBillsRollup>();
  for (const b of bills) {
    const r = map.get(b.vendorName) ?? {
      vendorName: b.vendorName,
      openBills: 0,
      totalDue: 0,
      overdueCount: 0,
      overdueAmount: 0,
      nextDueInDays: Infinity,
    };
    r.openBills += 1;
    r.totalDue += b.amount;
    if (b.status === "overdue") {
      r.overdueCount += 1;
      r.overdueAmount += b.amount;
    }
    if (b.dueInDays < r.nextDueInDays) r.nextDueInDays = b.dueInDays;
    map.set(b.vendorName, r);
  }
  return Array.from(map.values()).sort((a, b) => b.overdueAmount - a.overdueAmount || b.totalDue - a.totalDue);
}

// ───────────────────────────── Period Close ────────────────────────────

export interface CloseCheckItem {
  id: string;
  label: string;
  description: string;
  status: "complete" | "in-progress" | "blocked" | "pending";
  detail: string;
  href?: string;
}

export interface PeriodCloseResult {
  periodLabel: string;
  monthStartIso: string;
  monthEndIso: string;
  daysIntoMonth: number;
  totalDays: number;
  checks: CloseCheckItem[];
  completionPct: number;
}

export function buildPeriodClose(args: {
  jobs: Job[];
  payRunRecords: PayRunRecord[];
  heldRows: PayRunRow[];
  pendingFieldAdds: FieldAddSubmission[];
  holdbackStates: HoldbackState[];
  disputeStates: DisputeState[];
  bills: BillRow[];
}): PeriodCloseResult {
  const { jobs, payRunRecords, heldRows, pendingFieldAdds, holdbackStates, disputeStates, bills } = args;

  const now = new Date();
  // Close the *previous* month — that's the active reporting period.
  const periodEnd = new Date(now.getFullYear(), now.getMonth(), 0); // last day of prior month
  const periodStart = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), 1);
  const periodEndTs = new Date(periodEnd.getFullYear(), periodEnd.getMonth() + 1, 1).getTime();
  const periodStartTs = periodStart.getTime();
  const totalDays = Math.round((periodEndTs - periodStartTs) / MS_PER_DAY);
  const daysIntoMonth = Math.min(totalDays, Math.round((Date.now() - periodEndTs) / MS_PER_DAY) + 1);

  // 1. Invoices issued in period — % reconciled (paid or settled).
  let invoicesInPeriod = 0;
  let invoicesReconciled = 0;
  for (const j of jobs) {
    for (const inv of j.financials?.invoices ?? []) {
      if (!inv.issuedAt) continue;
      const ts = new Date(inv.issuedAt).getTime();
      if (ts < periodStartTs || ts >= periodEndTs) continue;
      invoicesInPeriod += 1;
      if (inv.status === "paid") invoicesReconciled += 1;
    }
  }
  const invoicePct = invoicesInPeriod > 0 ? (invoicesReconciled / invoicesInPeriod) * 100 : 100;

  // 2. Pay runs released in period vs still held.
  const payRunsReleasedInPeriod = payRunRecords.filter((r) => {
    const ts = new Date(r.releasedAt).getTime();
    return ts >= periodStartTs && ts < periodEndTs;
  });
  const stillHeld = heldRows.length;

  // 3. Field-adds pending older than period end.
  const stalePendingFieldAdds = pendingFieldAdds.filter((f) => {
    return f.createdAt && new Date(f.createdAt).getTime() < periodEndTs;
  });

  // 4. Open disputes.
  const openDisputes = disputeStates.filter((d) => d.status === "open");

  // 5. Holdbacks needing release review (aged 60+ days).
  const staleHoldbacks = holdbackStates.filter((h) => h.status === "active" && daysSince(h.lastEntryAt) >= 60);

  // 6. AP overdue at period close.
  const overdueBills = bills.filter((b) => b.status === "overdue");

  const checks: CloseCheckItem[] = [
    {
      id: "ar-reconcile",
      label: "AR reconciliation",
      description: "All period-issued invoices marked paid or settled.",
      status: invoicePct === 100 ? "complete" : invoicePct >= 80 ? "in-progress" : "pending",
      detail: `${invoicesReconciled} of ${invoicesInPeriod} reconciled · ${invoicePct.toFixed(0)}%`,
      href: "/finance/collections",
    },
    {
      id: "pay-runs",
      label: "Pay run authorization",
      description: "All releasable pay runs authorized or held with documented reason.",
      status: stillHeld === 0 ? "complete" : stillHeld <= 2 ? "in-progress" : "blocked",
      detail: `${payRunsReleasedInPeriod.length} released · ${stillHeld} still held`,
      href: "/finance/pay-runs",
    },
    {
      id: "field-adds",
      label: "Field-add backlog",
      description: "No field-add approvals pending from prior period.",
      status: stalePendingFieldAdds.length === 0 ? "complete" : "blocked",
      detail: stalePendingFieldAdds.length === 0
        ? "Backlog clear"
        : `${stalePendingFieldAdds.length} stale from prior period`,
      href: "/finance/approvals",
    },
    {
      id: "disputes",
      label: "Open disputes resolved",
      description: "All counterparty disputes closed or formally paused.",
      status: openDisputes.length === 0 ? "complete" : "in-progress",
      detail: openDisputes.length === 0
        ? "No open disputes"
        : `${openDisputes.length} open dispute${openDisputes.length === 1 ? "" : "s"}`,
      href: "/resolve",
    },
    {
      id: "holdbacks",
      label: "Holdback review",
      description: "Holdbacks aged 60+ days reviewed for release or extension.",
      status: staleHoldbacks.length === 0 ? "complete" : "in-progress",
      detail: staleHoldbacks.length === 0
        ? "All holdbacks current"
        : `${staleHoldbacks.length} aged 60+ days`,
      href: "/finance/approvals",
    },
    {
      id: "ap",
      label: "AP overdue clearance",
      description: "All overdue vendor bills paid or formally disputed.",
      status: overdueBills.length === 0 ? "complete" : "blocked",
      detail: overdueBills.length === 0
        ? "No overdue bills"
        : `${overdueBills.length} overdue bill${overdueBills.length === 1 ? "" : "s"}`,
      href: "/finance/bills",
    },
  ];

  const completionPct = (checks.filter((c) => c.status === "complete").length / checks.length) * 100;

  return {
    periodLabel: periodStart.toLocaleString("en-US", { month: "long", year: "numeric" }),
    monthStartIso: periodStart.toISOString(),
    monthEndIso: periodEnd.toISOString(),
    daysIntoMonth: Math.max(1, daysIntoMonth),
    totalDays,
    checks,
    completionPct,
  };
}
